// src/screens/admin/UsuariosAdmin.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { collection, getDocs, updateDoc, doc, getDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase/config";
import { useNavigate } from "react-router-dom";
import { 
  FaTrash, 
  FaHome, 
  FaSignOutAlt, 
  FaSearch, 
  FaUserCheck, 
  FaClock, 
  FaUserTimes,
  FaChevronLeft,
  FaChevronRight,
  FaArrowLeft,
  FaUserSlash
} from "react-icons/fa";

const APP_PRIMARY = "#b03060";

// --- HELPERS ESTÁTICOS (FUERA DEL COMPONENTE) ---
const esUsuarioEliminadoOInactivo = (u) => {
  const s = String(u.subscriptionStatus || u.estado || "").toLowerCase().trim();
  return Boolean(
    u.eliminado ||
    u.isDeleted ||
    u.status === "deleted" ||
    s === "inactivo" ||
    s === "eliminado"
  );
};

const esEstadoPendiente = (u) => {
  if (esUsuarioEliminadoOInactivo(u)) return false;
  const s = String(u.subscriptionStatus || "").toLowerCase().trim();
  return !s || s === "pending" || s === "pending_payment" || s === "payment_sent";
};

const calcularStatsTotales = (lista) => {
  let activos = 0;
  let pendientes = 0;
  let vencidos = 0;
  let inactivos = 0;

  lista.forEach((u) => {
    const s = String(u.subscriptionStatus || "").toLowerCase().trim();
    if (esUsuarioEliminadoOInactivo(u)) {
      inactivos++;
    } else if (s === "active" || s === "activo") {
      activos++;
    } else if (esEstadoPendiente(u)) {
      pendientes++;
    } else if (s === "expired" || s === "vencido") {
      vencidos++;
    } else {
      pendientes++;
    }
  });

  return { activos, pendientes, vencidos, inactivos };
};

export default function UsuariosAdmin() {
  const navigate = useNavigate();
  const montadoRef = useRef(true);

  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [tab, setTab] = useState("pendiente");
  const [paginaActual, setPaginaActual] = useState(1);
  const [usuarioEliminar, setUsuarioEliminar] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [procesandoId, setProcesandoId] = useState(null);
  const [errorAcceso, setErrorAcceso] = useState("");
  const [mensajeExito, setMensajeExito] = useState("");

  const [stats, setStats] = useState({
    activos: 0,
    pendientes: 0,
    vencidos: 0,
    inactivos: 0
  });

  const usuariosPorPagina = 8;

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  const cargarUsuarios = useCallback(async () => {
    try {
      if (montadoRef.current) setCargando(true);
      const snapshot = await getDocs(collection(db, "usuarios"));
      const lista = [];

      snapshot.forEach((docu) => {
        const d = docu.data() || {};
        lista.push({
          id: docu.id,
          ...d,
          nombre: d.nombre || "",
          apellido: d.apellido || "",
          email: d.email || "",
          plan: d.plan || d.planSeleccionado || d.planNombreReportado || "",
          subscriptionStatus: d.subscriptionStatus || d.estado || "pending_payment"
        });
      });

      if (montadoRef.current) {
        setUsuarios(lista);
        setStats(calcularStatsTotales(lista));
      }
    } catch {
      if (montadoRef.current) {
        setErrorAcceso("No tienes permisos suficientes o las directivas de seguridad impiden la lectura.");
      }
    } finally {
      if (montadoRef.current) setCargando(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, "usuarios", user.uid));
        if (!userSnap.exists()) {
          navigate("/login", { replace: true });
          return;
        }

        const data = userSnap.data();
        const esAdmin = 
          data.isAdmin === true || 
          data.rol === "admin" || 
          user.email === "marianafitness.app@gmail.com";

        if (!esAdmin) {
          navigate("/home", { replace: true });
          return;
        }

        cargarUsuarios();
      } catch {
        navigate("/login", { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate, cargarUsuarios]);

  const filtrarUsuarios = useCallback(() => {
    let lista = [...usuarios];

    if (tab === "pendiente") {
      lista = lista.filter((u) => esEstadoPendiente(u));
    } else if (tab === "activo") {
      lista = lista.filter((u) => {
        if (esUsuarioEliminadoOInactivo(u)) return false;
        const s = String(u.subscriptionStatus || "").toLowerCase().trim();
        return s === "active" || s === "activo";
      });
    } else if (tab === "vencido") {
      lista = lista.filter((u) => {
        if (esUsuarioEliminadoOInactivo(u)) return false;
        const s = String(u.subscriptionStatus || "").toLowerCase().trim();
        return s === "expired" || s === "vencido";
      });
    } else if (tab === "inactivo") {
      lista = lista.filter((u) => esUsuarioEliminadoOInactivo(u));
    }

    if (busqueda.trim() !== "") {
      const query = busqueda.toLowerCase().trim();
      lista = lista.filter(
        (u) =>
          String(u.nombre || "").toLowerCase().includes(query) ||
          String(u.apellido || "").toLowerCase().includes(query) ||
          String(u.email || "").toLowerCase().includes(query)
      );
    }

    setUsuariosFiltrados(lista);
    setPaginaActual(1);
  }, [usuarios, tab, busqueda]);

  useEffect(() => {
    filtrarUsuarios();
  }, [filtrarUsuarios]);

  const activarUsuario = async (usuario) => {
    if (procesandoId) return;
    setProcesandoId(usuario.id);
    setErrorAcceso("");
    setMensajeExito("");

    try {
      const ref = doc(db, "usuarios", usuario.id);
      const hoy = new Date();
      let inicio = hoy;

      if (usuario.planEnd) {
        const fechaFin = usuario.planEnd.toDate ? usuario.planEnd.toDate() : new Date(usuario.planEnd);
        if (fechaFin > hoy) inicio = fechaFin;
      }

      const planRaw = String(usuario.plan || usuario.planSeleccionado || usuario.planNombreReportado || "").toLowerCase();
      let diasDuracion = 30;

      if (planRaw.includes("trial") || planRaw.includes("7") || planRaw.includes("seman")) {
        diasDuracion = 7;
      } else if (planRaw.includes("semestr")) {
        diasDuracion = 180;
      } else if (planRaw.includes("anual") || planRaw.includes("ano") || planRaw.includes("año")) {
        diasDuracion = 365;
      }

      const fin = new Date(inicio);
      fin.setDate(inicio.getDate() + diasDuracion);

      const esTrial = diasDuracion === 7;

      await updateDoc(ref, {
        subscriptionStatus: "active",
        estado: "active",
        eliminado: false,
        isDeleted: false,
        ...(esTrial ? { haUsadoTrial: true, trialUtilizado: true } : {}),
        plan: usuario.plan || (esTrial ? "trial" : "mensual"),
        planStart: Timestamp.fromDate(inicio),
        planEnd: Timestamp.fromDate(fin),
        activadoPorAdminEn: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      if (montadoRef.current) {
        setMensajeExito(`Acceso activado correctamente por ${diasDuracion} días.`);
        setTimeout(() => setMensajeExito(""), 4000);
      }

      await cargarUsuarios();
    } catch {
      if (montadoRef.current) {
        setErrorAcceso("No fue posible activar la suscripción del usuario.");
      }
    } finally {
      if (montadoRef.current) setProcesandoId(null);
    }
  };

  const confirmarEliminar = async () => {
    if (!usuarioEliminar || procesandoId) return;
    setProcesandoId(usuarioEliminar.id);
    setErrorAcceso("");

    try {
      const ref = doc(db, "usuarios", usuarioEliminar.id);
      await updateDoc(ref, {
        eliminado: true,
        isDeleted: true,
        status: "deleted",
        subscriptionStatus: "inactivo",
        estado: "inactivo",
        fechaBaja: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setUsuarioEliminar(null);
      await cargarUsuarios();
    } catch {
      if (montadoRef.current) {
        setErrorAcceso("No fue posible desactivar al usuario.");
      }
    } finally {
      if (montadoRef.current) setProcesandoId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch {
      navigate("/login", { replace: true });
    }
  };

  const renderEstado = (u) => {
    if (esUsuarioEliminadoOInactivo(u)) {
      return <span style={{ ...styles.badge, ...styles.badgeDeleted }}>Inactivo / Baja</span>;
    }
    const estado = String(u.subscriptionStatus || "").toLowerCase().trim();
    if (estado === "active" || estado === "activo") {
      return <span style={{ ...styles.badge, ...styles.badgeActive }}>Activo</span>;
    }
    if (estado === "pending" || estado === "pending_payment") {
      return <span style={{ ...styles.badge, ...styles.badgePending }}>Pendiente pago</span>;
    }
    if (estado === "payment_sent") {
      return <span style={{ ...styles.badge, ...styles.badgePaymentSent }}>Pago notificado</span>;
    }
    if (estado === "expired" || estado === "vencido") {
      return <span style={{ ...styles.badge, ...styles.badgeExpired }}>Vencido</span>;
    }
    return <span style={{ ...styles.badge, ...styles.badgePending }}>Pendiente</span>;
  };

  const calcularDiasRestantes = (u) => {
    if (esUsuarioEliminadoOInactivo(u)) return <span style={{ color: "#64748b" }}>-</span>;
    if (!u.planEnd) return "-";
    const fechaFin = u.planEnd.toDate ? u.planEnd.toDate() : new Date(u.planEnd);
    const hoy = new Date();
    const diferencia = fechaFin - hoy;
    const dias = Math.ceil(diferencia / (1000 * 60 * 60 * 24));
    if (dias <= 0) return <span style={{ color: "#dc2626", fontWeight: "600" }}>Vencido</span>;
    return `${dias} días`;
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    try {
      if (fecha.toDate) return fecha.toDate().toLocaleDateString("es-CL");
      return new Date(fecha).toLocaleDateString("es-CL");
    } catch {
      return "-";
    }
  };

  const totalPaginas = Math.ceil(usuariosFiltrados.length / usuariosPorPagina) || 1;
  const inicio = (paginaActual - 1) * usuariosPorPagina;
  const usuariosPagina = usuariosFiltrados.slice(inicio, inicio + usuariosPorPagina);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <button onClick={() => navigate("/admin")} style={styles.backButton}>
            <FaArrowLeft size={13} /> Volver al panel
          </button>
          <span style={styles.headerBadge}>Administración</span>
        </div>

        <h2 style={styles.title}>Gestión de Usuarios</h2>
        <p style={styles.subtitle}>Supervisa los accesos, planes activos y comprobantes pendientes</p>

        {errorAcceso && <div style={styles.errorBanner}>{errorAcceso}</div>}
        {mensajeExito && <div style={styles.successBanner}>{mensajeExito}</div>}

        <div style={styles.statsContainer}>
          <button
            type="button"
            onClick={() => setTab("activo")}
            style={{
              ...styles.statCard,
              border: tab === "activo" ? "2px solid #16a34a" : "1px solid #f1f5f9",
              backgroundColor: tab === "activo" ? "#f0fdf4" : "#ffffff"
            }}
          >
            <div style={{ ...styles.statIconBox, background: tab === "activo" ? "#dcfce7" : "#f0fdf4" }}>
              <FaUserCheck size={20} color="#16a34a" />
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ ...styles.statNumber, color: "#16a34a" }}>{stats.activos}</div>
              <div style={styles.statLabel}>Usuarios activos</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setTab("pendiente")}
            style={{
              ...styles.statCard,
              border: tab === "pendiente" ? `2px solid ${APP_PRIMARY}` : "1px solid #f1f5f9",
              backgroundColor: tab === "pendiente" ? "#fdf2f8" : "#ffffff"
            }}
          >
            <div style={{ ...styles.statIconBox, background: tab === "pendiente" ? "#fce7f3" : "#fffbeb" }}>
              <FaClock size={20} color={tab === "pendiente" ? APP_PRIMARY : "#d97706"} />
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ ...styles.statNumber, color: tab === "pendiente" ? APP_PRIMARY : "#d97706" }}>
                {stats.pendientes}
              </div>
              <div style={styles.statLabel}>Pagos pendientes</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setTab("vencido")}
            style={{
              ...styles.statCard,
              border: tab === "vencido" ? "2px solid #dc2626" : "1px solid #f1f5f9",
              backgroundColor: tab === "vencido" ? "#fef2f2" : "#ffffff"
            }}
          >
            <div style={{ ...styles.statIconBox, background: tab === "vencido" ? "#fee2e2" : "#fef2f2" }}>
              <FaUserTimes size={20} color="#dc2626" />
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ ...styles.statNumber, color: "#dc2626" }}>{stats.vencidos}</div>
              <div style={styles.statLabel}>Planes vencidos</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setTab("inactivo")}
            style={{
              ...styles.statCard,
              border: tab === "inactivo" ? "2px solid #64748b" : "1px solid #f1f5f9",
              backgroundColor: tab === "inactivo" ? "#f8fafc" : "#ffffff"
            }}
          >
            <div style={{ ...styles.statIconBox, background: tab === "inactivo" ? "#e2e8f0" : "#f1f5f9" }}>
              <FaUserSlash size={20} color="#64748b" />
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ ...styles.statNumber, color: "#64748b" }}>{stats.inactivos}</div>
              <div style={styles.statLabel}>Inactivos / Bajas</div>
            </div>
          </button>
        </div>

        <div style={styles.filterSection}>
          <div style={styles.tabs}>
            <button style={tab === "pendiente" ? styles.tabActive : styles.tab} onClick={() => setTab("pendiente")}>
              Pendientes ({stats.pendientes})
            </button>
            <button style={tab === "activo" ? styles.tabActive : styles.tab} onClick={() => setTab("activo")}>
              Activos ({stats.activos})
            </button>
            <button style={tab === "vencido" ? styles.tabActive : styles.tab} onClick={() => setTab("vencido")}>
              Vencidos ({stats.vencidos})
            </button>
            <button style={tab === "inactivo" ? styles.tabActive : styles.tab} onClick={() => setTab("inactivo")}>
              Inactivos ({stats.inactivos})
            </button>
          </div>

          <div style={styles.searchWrapper}>
            <FaSearch style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar por nombre o correo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={styles.search}
            />
          </div>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Nombre</th>
                <th style={styles.th}>Apellido</th>
                <th style={styles.th}>Correo</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Plan</th>
                <th style={styles.th}>Inicio</th>
                <th style={styles.th}>Vencimiento</th>
                <th style={styles.th}>Días</th>
                <th style={{ ...styles.th, textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cargando ? (
                <tr>
                  <td colSpan="9" style={styles.emptyRow}>Cargando usuarios...</td>
                </tr>
              ) : usuariosPagina.length === 0 ? (
                <tr>
                  <td colSpan="9" style={styles.emptyRow}>No se encontraron usuarios en esta categoría.</td>
                </tr>
              ) : (
                usuariosPagina.map((u) => {
                  const enProceso = procesandoId === u.id;
                  return (
                    <tr key={u.id} style={styles.row}>
                      <td style={{ ...styles.td, fontWeight: 600, color: "#1e293b" }}>{u.nombre || "-"}</td>
                      <td style={{ ...styles.td, color: "#1e293b" }}>{u.apellido || "-"}</td>
                      <td style={{ ...styles.td, color: "#64748b" }}>{u.email}</td>
                      <td style={styles.td}>{renderEstado(u)}</td>
                      <td style={{ ...styles.td, textTransform: "capitalize" }}>{u.plan || "-"}</td>
                      <td style={styles.td}>{formatearFecha(u.planStart)}</td>
                      <td style={styles.td}>{formatearFecha(u.planEnd)}</td>
                      <td style={styles.td}>{calcularDiasRestantes(u)}</td>
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          <button style={styles.verFicha} onClick={() => navigate(`/admin/usuarios/${u.id}`)}>
                            Ver ficha
                          </button>
                          {(esEstadoPendiente(u) || esUsuarioEliminadoOInactivo(u)) && (
                            <button
                              style={{ ...styles.activarBtn, opacity: enProceso ? 0.6 : 1 }}
                              disabled={enProceso}
                              onClick={() => activarUsuario(u)}
                            >
                              {enProceso ? "Activando..." : "Activar"}
                            </button>
                          )}
                          {!esUsuarioEliminadoOInactivo(u) && (
                            <button
                              style={{ ...styles.deleteIcon, opacity: enProceso ? 0.6 : 1 }}
                              disabled={enProceso}
                              onClick={() => setUsuarioEliminar(u)}
                            >
                              <FaTrash size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div style={styles.pagination}>
            <button
              style={{ ...styles.pageButton, opacity: paginaActual === 1 ? 0.4 : 1 }}
              disabled={paginaActual === 1}
              onClick={() => setPaginaActual(paginaActual - 1)}
            >
              <FaChevronLeft size={11} /> Anterior
            </button>
            <span style={styles.pageInfo}>Página <b>{paginaActual}</b> de <b>{totalPaginas}</b></span>
            <button
              style={{ ...styles.pageButton, opacity: paginaActual === totalPaginas ? 0.4 : 1 }}
              disabled={paginaActual === totalPaginas}
              onClick={() => setPaginaActual(paginaActual + 1)}
            >
              Siguiente <FaChevronRight size={11} />
            </button>
          </div>
        )}
      </div>

      {usuarioEliminar && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ margin: "0 0 10px 0", color: "#1e293b" }}>¿Dar de baja por inactividad?</h3>
            <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 20px 0", lineHeight: 1.5 }}>
              El alumno <b>{usuarioEliminar.email}</b> quedará inhabilitado.
            </p>
            <div style={styles.modalButtons}>
              <button style={styles.cancelButton} onClick={() => setUsuarioEliminar(null)}>Cancelar</button>
              <button style={styles.deleteConfirm} onClick={confirmarEliminar}>Sí, dar de baja</button>
            </div>
          </div>
        </div>
      )}

      <div style={navDockStyles.dockContainer}>
        <div style={navDockStyles.dockPill}>
          <button type="button" onClick={() => navigate("/admin")} style={navDockStyles.dockButton}>
            <div style={navDockStyles.iconWrapper}><FaHome size={15} /></div>
            <span>Inicio</span>
          </button>
          <div style={navDockStyles.divider} />
          <button type="button" onClick={handleLogout} style={{ ...navDockStyles.dockButton, color: "#dc2626" }}>
            <div style={{ ...navDockStyles.iconWrapper, backgroundColor: "#fee2e2", color: "#dc2626" }}>
              <FaSignOutAlt size={14} />
            </div>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Estilos limpios y profesionales
const styles = {
  page: { background: "#f8fafc", minHeight: "100vh", padding: "24px 16px 120px 16px", boxSizing: "border-box", fontFamily: "'Poppins', sans-serif" },
  container: { maxWidth: 1200, margin: "0 auto", background: "#ffffff", padding: "32px 28px", borderRadius: 22, boxShadow: "0 8px 30px rgba(0, 0, 0, 0.04)", border: "1px solid #f1f5f9" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  backButton: { background: "#fdf2f8", color: APP_PRIMARY, border: "1px solid #fbcfe8", padding: "8px 16px", borderRadius: 50, cursor: "pointer", fontWeight: 600, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 },
  headerBadge: { fontSize: 11, fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase", color: APP_PRIMARY, background: "#fdf2f8", padding: "4px 12px", borderRadius: 20 },
  title: { color: "#1e293b", fontSize: 26, fontWeight: 700, margin: "0 0 6px 0", letterSpacing: "-0.3px" },
  subtitle: { color: "#64748b", fontSize: 14, margin: "0 0 24px 0" },
  errorBanner: { backgroundColor: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px 16px", borderRadius: 12, fontSize: 13, marginBottom: 20, fontWeight: 500 },
  successBanner: { backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "12px 16px", borderRadius: 12, fontSize: 13, marginBottom: 20, fontWeight: 600 },
  statsContainer: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 28 },
  statCard: { padding: "18px 20px", borderRadius: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.03)", display: "flex", alignItems: "center", gap: 16, cursor: "pointer", outline: "none", fontFamily: "inherit" },
  statIconBox: { width: 48, height: 48, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statNumber: { fontSize: 28, fontWeight: 700, lineHeight: 1 },
  statLabel: { marginTop: 4, fontSize: 13, color: "#64748b", fontWeight: 500 },
  filterSection: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 20 },
  tabs: { display: "flex", gap: 8, flexWrap: "wrap" },
  tab: { padding: "8px 18px", border: "1.5px solid #e2e8f0", background: "#ffffff", color: "#64748b", borderRadius: 50, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  tabActive: { padding: "8px 18px", border: `1.5px solid ${APP_PRIMARY}`, background: APP_PRIMARY, color: "#ffffff", borderRadius: 50, cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: "0 3px 10px rgba(176, 48, 96, 0.25)" },
  searchWrapper: { position: "relative", display: "flex", alignItems: "center", minWidth: 260 },
  searchIcon: { position: "absolute", left: 14, color: "#94a3b8", fontSize: 13 },
  search: { padding: "10px 16px 10px 38px", borderRadius: 50, border: "1.5px solid #e2e8f0", width: "100%", fontSize: 13, outline: "none", color: "#1e293b", background: "#ffffff", boxSizing: "border-box" },
  tableWrapper: { overflowX: "auto", borderRadius: 14, border: "1px solid #f1f5f9" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 850 },
  th: { textAlign: "left", padding: "14px 16px", background: "#f8fafc", color: "#475569", fontSize: 12, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" },
  td: { textAlign: "left", padding: "14px 16px", fontSize: 13, color: "#334155" },
  row: { borderBottom: "1px solid #f1f5f9" },
  emptyRow: { textAlign: "center", padding: 36, color: "#94a3b8", fontSize: 14 },
  actions: { display: "flex", alignItems: "center", gap: 8 },
  verFicha: { background: "#fdf2f8", color: APP_PRIMARY, border: "1px solid #fbcfe8", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 },
  activarBtn: { background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 },
  deleteIcon: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", cursor: "pointer", padding: "7px 10px", borderRadius: 6, display: "inline-flex", alignItems: "center", justifyContent: "center" },
  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 28 },
  pageButton: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", border: "1px solid #e2e8f0", background: "#ffffff", color: "#475569", borderRadius: 50, fontSize: 13, fontWeight: 600 },
  pageInfo: { fontSize: 13, color: "#64748b" },
  badge: { display: "inline-block", padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.2px" },
  badgeActive: { background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" },
  badgePending: { background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" },
  badgePaymentSent: { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" },
  badgeExpired: { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
  badgeDeleted: { background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1" },
  modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 16 },
  modal: { background: "#ffffff", padding: 28, borderRadius: 20, width: "100%", maxWidth: 380, textAlign: "center", boxShadow: "0 20px 40px rgba(0,0,0,0.15)", border: "1px solid #f1f5f9" },
  modalButtons: { display: "flex", justifyContent: "center", gap: 12 },
  cancelButton: { padding: "10px 18px", border: "1px solid #e2e8f0", background: "#ffffff", color: "#475569", borderRadius: 50, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  deleteConfirm: { padding: "10px 18px", border: "none", background: "#dc2626", color: "#ffffff", borderRadius: 50, cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: "0 3px 10px rgba(220, 38, 38, 0.25)" }
};

const navDockStyles = {
  dockContainer: { position: "fixed", bottom: 20, left: 0, width: "100%", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, pointerEvents: "none", padding: "0 16px", boxSizing: "border-box" },
  dockPill: { pointerEvents: "auto", display: "flex", alignItems: "center", gap: 8, backgroundColor: "rgba(255, 255, 255, 0.94)", backdropFilter: "blur(12px)", border: "1.5px solid rgba(241, 245, 249, 0.95)", borderRadius: 50, padding: "6px 12px", boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)" },
  dockButton: { display: "inline-flex", alignItems: "center", gap: 8, border: "none", borderRadius: 50, padding: "8px 16px", cursor: "pointer", fontSize: "13px", fontFamily: "'Poppins', sans-serif", backgroundColor: "transparent", color: "#64748b", fontWeight: 600 },
  iconWrapper: { width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f1f5f9", color: "#64748b" },
  divider: { width: 1, height: 22, backgroundColor: "#e2e8f0", margin: "0 2px" }
};